/**
 * Os filtros e a paginação da Oficina (11/09/2026).
 *
 * ## O defeito
 *
 * A oficina chegou a **115 receitas** — 78 da squad, 22 poções e 15 apetrechos — numa página só, e
 * o único recorte era a categoria. Achar "o que dá para fazer com a Alga Costeira que sobrou" era
 * rolar a página inteira lendo carta por carta; das 115, o jogador de teste conseguia fazer **13**,
 * espalhadas entre as outras.
 *
 * ## Por que função pura, e fora do componente
 *
 * A mesma conta serve a duas coisas: a lista e o **número em cada filtro** ("Para a squad (47)"),
 * que é a lista contada com um filtro a menos. Dentro do componente seriam duas versões da regra;
 * aqui é uma, e o teste a exercita sem montar a tela.
 *
 * Nada aqui decide se a receita **pode** ser feita: `vezesPossiveis` vem avaliado do servidor, e
 * "dá para fazer agora" só lê esse número.
 */

export const RECEITAS_POR_PAGINA = 12;

/** O estado sem filtro nenhum. `nivel: 0` é "todos" — não existe receita de nível 0. */
export const FILTROS_VAZIOS = Object.freeze({
  categoria: 'todos',
  tipo: '',
  guilda: '',
  raridade: '',
  nivel: 0,
  busca: '',
  soProntas: false,
});

/** A escada do banco (em inglês, ver `raridade.js`), da mais comum para a mais rara. */
const ORDEM_DE_RARIDADE = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythic'];

/** Os tipos de cada categoria, na ordem em que o filtro os oferece. */
const TIPOS_POR_CATEGORIA = {
  integrante: ['ferramenta', 'armadura', 'acessorio', 'consumivel'],
  apetrecho: ['anzol', 'boia', 'caixa'],
  pocao: ['poder', 'taxaDeCaptura', 'critico', 'sorte', 'experiencia', 'energia'],
};

const ORDEM_DE_GUILDA = ['mercenario', 'geologo', 'paleontologo', 'arqueologo', 'botanico'];

/**
 * O "tipo" de uma receita dentro da categoria dela: o slot do equipamento, o slot do apetrecho ou
 * o efeito da poção. Consumível de squad não tem slot — ele é o próprio tipo.
 */
export function tipoDaReceita(receita) {
  const p = receita.produz || {};
  if (p.categoria === 'apetrecho') return p.apetrecho?.slot || '';
  if (p.categoria === 'pocao') return p.pocao?.efeito || '';
  return p.tipo === 'consumivel' ? 'consumivel' : p.slot || '';
}

/** Minúsculo e sem acento: "pocao" acha "Poção", e "Sal marinho" acha "Sal Marinho". */
export function normalizar(texto) {
  return String(texto || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * A busca olha o que sai **e o que entra**: o nome do produto, o da receita e os dos
 * ingredientes. O segundo é o que responde à pergunta de quem volta de uma expedição — "o que eu
 * faço com isto?" —, que era justamente a que a tela não respondia.
 */
function casaComBusca(receita, busca) {
  if (!busca) return true;
  const nomes = [receita.produz?.nome, receita.nome, ...(receita.ingredientes || []).map((i) => i.nome)];
  return nomes.some((nome) => normalizar(nome).includes(busca));
}

/**
 * As receitas que passam em todos os filtros, **em ordem de nível** (a ordem do servidor vale
 * dentro do nível — o `sort` é estável).
 *
 * `ignorar` tira filtros da conta: é como se mede o número ao lado de cada opção. A categoria
 * leva junto `tipo` e `guilda`, que só existem dentro dela.
 *
 * @param {object[]} receitas
 * @param {object} filtros  forma de `FILTROS_VAZIOS`
 * @param {{ignorar?: string[]}} [opcoes]
 */
export function filtrarReceitas(receitas, filtros, { ignorar = [] } = {}) {
  const f = { ...FILTROS_VAZIOS, ...filtros };
  const fora = new Set(ignorar);
  if (fora.has('categoria')) {
    fora.add('tipo');
    fora.add('guilda');
  }
  const busca = fora.has('busca') ? '' : normalizar(f.busca);

  return (receitas || [])
    .filter((r) => {
      const p = r.produz || {};
      if (!fora.has('categoria') && f.categoria !== 'todos' && p.categoria !== f.categoria) return false;
      if (!fora.has('tipo') && f.tipo && tipoDaReceita(r) !== f.tipo) return false;
      if (!fora.has('guilda') && f.guilda) {
        // "qualquer" é a peça sem dono — a que serve a todo integrante.
        if (f.guilda === 'qualquer' ? p.guilda : p.guilda !== f.guilda) return false;
      }
      if (!fora.has('raridade') && f.raridade && p.raridade !== f.raridade) return false;
      if (!fora.has('nivel') && f.nivel && r.nivel !== Number(f.nivel)) return false;
      if (!fora.has('soProntas') && f.soProntas && !(r.vezesPossiveis > 0)) return false;
      return casaComBusca(r, busca);
    })
    .sort((a, b) => a.nivel - b.nivel);
}

/**
 * O que cada `select` oferece — só o que existe no catálogo, para nenhuma opção dar lista vazia
 * por construção. Tipo e guilda dependem da categoria escolhida; nível e raridade, não.
 */
export function opcoesDaOficina(receitas, categoria) {
  const todas = receitas || [];
  const daCategoria = categoria === 'todos' ? [] : todas.filter((r) => r.produz?.categoria === categoria);
  const presentes = (lista, chave) => new Set(lista.map(chave));

  const tipos = presentes(daCategoria, tipoDaReceita);
  const guildas = presentes(daCategoria, (r) => r.produz?.guilda || null);
  const raridades = presentes(todas, (r) => r.produz?.raridade);

  return {
    tipos: (TIPOS_POR_CATEGORIA[categoria] || []).filter((t) => tipos.has(t)),
    // Guilda só faz sentido na squad, e só se alguma peça tiver dono.
    guildas: categoria === 'integrante' ? ORDEM_DE_GUILDA.filter((g) => guildas.has(g)) : [],
    raridades: ORDEM_DE_RARIDADE.filter((r) => raridades.has(r)),
    niveis: [...presentes(todas, (r) => r.nivel)].sort((a, b) => a - b),
  };
}

/** Algum filtro fora do padrão? É o que acende o "Limpar" e escolhe a frase do vazio. */
export function temFiltro(filtros) {
  const f = { ...FILTROS_VAZIOS, ...filtros };
  return Object.keys(FILTROS_VAZIOS).some((k) => {
    if (k === 'nivel') return Number(f.nivel) > 0;
    if (k === 'busca') return normalizar(f.busca) !== '';
    return f[k] !== FILTROS_VAZIOS[k];
  });
}

/**
 * Um recorte da lista. A página pedida é **encaixada** no que existe: depois de um craft com
 * "dá para fazer agora" ligado, a receita sai da lista e a última página pode deixar de existir —
 * mostrar uma página vazia ali seria a tela dizendo que não há nada.
 */
export function paginar(lista, pagina, porPagina = RECEITAS_POR_PAGINA) {
  const paginas = Math.max(1, Math.ceil(lista.length / porPagina));
  const atual = Math.min(Math.max(1, Number(pagina) || 1), paginas);
  const inicio = (atual - 1) * porPagina;
  return { itens: lista.slice(inicio, inicio + porPagina), pagina: atual, paginas };
}

/** `[{ nivel, receitas }]` — o título "Nível 3" continua, agora dentro de cada página. */
export function agruparPorNivel(receitas) {
  const grupos = [];
  for (const r of receitas) {
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.nivel === r.nivel) ultimo.receitas.push(r);
    else grupos.push({ nivel: r.nivel, receitas: [r] });
  }
  return grupos;
}
